import React, {useEffect, useMemo, useState} from 'react'
import {useHistory, useLocation} from 'react-router-dom'
import {loadApplication, loadHousehold, loadReviewSummary} from '../../api'
import {IApplicationFormData, normalizeApplicationFormData} from '../../models/application-form'
import '../../style/application.scss'
import WizardHeader from '../wizard/WizardHeader'
import {SubmitToPropertyContent} from './components/SubmitToPropertyContent'
import {
  getSessionIdFromSearch,
  RENTER_HOME_PATH,
  type SubmitToPropertyPrefill,
} from './renterHomeUtils'

type SubmitToPropertyLocationState = {
  initialApplicationData?: IApplicationFormData
  submissionPrefill?: SubmitToPropertyPrefill
}

const MAX_CONTENT_WIDTH = 560

const SubmitToPropertyPage: React.FC = () => {
  const history = useHistory()
  const location = useLocation<SubmitToPropertyLocationState>()
  const sessionId = useMemo(() => getSessionIdFromSearch(location.search), [location.search])
  const initialDraft = useMemo(() => {
    const draft = location.state?.initialApplicationData
    if (!draft) {
      return null
    }

    const normalized = normalizeApplicationFormData({
      ...draft,
      sessionId: draft.sessionId ?? sessionId,
    })

    if (sessionId && normalized.sessionId && normalized.sessionId !== sessionId) {
      return null
    }

    return normalized
  }, [location.state, sessionId])
  const [draft, setDraft] = useState<IApplicationFormData | null>(initialDraft)
  const [isLoading, setIsLoading] = useState(Boolean(sessionId && !initialDraft))
  const [loadError, setLoadError] = useState<string | null>(null)

  const returnToRenterHome = () => {
    const search = sessionId ? `?sessionId=${encodeURIComponent(sessionId)}` : ''
    history.replace(`${RENTER_HOME_PATH}${search}`)
  }

  useEffect(() => {
    if (initialDraft || !sessionId) {
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setLoadError(null)

    const loadDraft = async () => {
      try {
        const [loaded, reviewSummary, householdSummary] = await Promise.all([
          loadApplication(sessionId),
          loadReviewSummary(sessionId).catch(() => null),
          loadHousehold(sessionId).catch(() => null),
        ])
        if (!cancelled) {
          setDraft(
            normalizeApplicationFormData({
              ...loaded,
              sessionId: loaded.sessionId ?? sessionId,
              reviewSummary: householdSummary ?? reviewSummary ?? loaded.reviewSummary,
            })
          )
        }
      } catch {
        if (!cancelled) {
          setLoadError('Unable to load your Doora Card. Please return to your dashboard and retry.')
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    loadDraft()

    return () => {
      cancelled = true
    }
  }, [initialDraft, sessionId])

  return (
    <>
      <WizardHeader
        step={1}
        totalSteps={1}
        title='Send'
        showProgressBar={true}
        onBack={returnToRenterHome}
      />
      <main className='wizard-page px-3 px-md-6 py-6 py-md-10'>
        <div className='mx-auto w-100' style={{maxWidth: MAX_CONTENT_WIDTH}}>
          {isLoading ? (
            <div className='text-center text-muted py-10'>Loading your Doora Card...</div>
          ) : loadError || !sessionId || !draft ? (
            <div className='bg-white rounded-3 border p-6 text-center'>
              <p className='text-danger mb-4'>
                {loadError ?? 'Your secure session is unavailable.'}
              </p>
              <button type='button' className='btn btn-light' onClick={returnToRenterHome}>
                Back to your dashboard
              </button>
            </div>
          ) : (
            <SubmitToPropertyContent
              sessionId={sessionId}
              draft={draft}
              onClose={returnToRenterHome}
              failureActionLabel='Back to dashboard'
              initialDetails={location.state?.submissionPrefill}
            />
          )}
        </div>
      </main>
    </>
  )
}

export default SubmitToPropertyPage
